import * as THREE from 'three'

import { UVBuilder } from './uv-builder'

interface MainFrameSettings {
  artworkWidth: number
  artworkHeight: number
  borderTopSize: number
  borderRightSize: number
  borderBottomSize: number
  borderLeftSize: number
  passepartoutTopSize: number
  passepartoutRightSize: number
  passepartoutBottomSize: number
  passepartoutLeftSize: number
  depth: number
  inset: number
}

interface BackFaceFrameSettings {
  generateBackFace: boolean
}

type FullFrameSettings = MainFrameSettings & BackFaceFrameSettings

export type PartialMainFrameSettings = Partial<MainFrameSettings>

export type PartialFullFrameSettings = Partial<FullFrameSettings>

export const defaultFrameSettings: FullFrameSettings = {
  artworkWidth: 2,
  artworkHeight: 1,
  borderTopSize: 0.2,
  borderRightSize: 0.2,
  borderBottomSize: 0.2,
  borderLeftSize: 0.2,
  passepartoutTopSize: 0,
  passepartoutRightSize: 0,
  passepartoutBottomSize: 0,
  passepartoutLeftSize: 0,
  depth: 0.3,
  inset: 0.2,
  generateBackFace: false,
}

// Frame texture size in meters
const textureSize = 0.1

const updateFrameUVs = (frameGeometry: THREE.Geometry) => {
  const builder = new UVBuilder(frameGeometry)
  const frameBoundingBox = frameGeometry.boundingBox

  if (!frameBoundingBox) {
    throw new Error('Frame geometry bound box not set.')
  }

  const centerPoint = new THREE.Vector2(0.5, 0.5)
  const scalingFactor = 1 / textureSize

  let outFacingSurfaceWidth = 0
  let frontFacingSurfaceWidth = 0

  const uvs = [
    // 1. Out-facing surfaces
    // 1.1. Top
    // 1.1.1. B.lt (0) -> F.rt (5) -> B.rt (1)
    builder
      .planar('y', [0, 5, 1], scalingFactor)
      .tap((builder) => {
        outFacingSurfaceWidth = builder.getUvBoundingBoxSize().height
      })
      .move('a', 0, null)
      .toUv(),
    // 1.1.2. F.lt (4) -> F.rt (5) -> B.lt (0)
    builder.planar('y', [4, 5, 0], scalingFactor).move('c', 0, null).toUv(),
    // 1.2. Right
    // 1.2.1. B.rt (1) -> F.rb (6) -> B.rb (2)
    builder
      .planar('x', [1, 6, 2], scalingFactor)
      .rotate(THREE.MathUtils.degToRad(90), centerPoint)
      .move('a', 0, null)
      .toUv(),
    // 1.2.2. B.rt (1) -> F.rt (5) -> F.rb (6)
    builder
      .planar('x', [1, 5, 6], scalingFactor)
      .rotate(THREE.MathUtils.degToRad(90), centerPoint)
      .move('a', 0, null)
      .toUv(),
    // 1.3. Bottom
    // 1.3.1. B.rb (2) -> F.lb (7) -> B.lb (3)
    builder
      .planar('y', [2, 7, 3], scalingFactor)
      .move('a', 0, null)
      .mirrorX(0)
      .toUv(),
    // 1.3.2. B.rb (2) -> F.rb (6) -> F.lb (7)
    builder
      .planar('y', [2, 6, 7], scalingFactor)
      .move('a', 0, null)
      .mirrorX(0)
      .toUv(),
    // 1.4. Left
    // 1.4.1. B.lt (0) -> B.lb (3) -> F.lt (4)
    builder
      .planar('x', [0, 3, 4], scalingFactor)
      .rotate(THREE.MathUtils.degToRad(90), centerPoint)
      .move('b', 0, null)
      .mirrorX(0)
      .toUv(),
    // 1.4.2. F.lt (4) -> B.lb (3) -> F.lb (7)
    builder
      .planar('x', [4, 3, 7], scalingFactor)
      .rotate(THREE.MathUtils.degToRad(90), centerPoint)
      .move('b', 0, null)
      .mirrorX(0)
      .toUv(),

    // 2. Front-facing surfaces
    // 2.1. Top
    // 2.1.1. F.lt (4) -> I.rt (9) -> F.rt (5)
    builder
      .planar('z', [4, 9, 5], scalingFactor)
      .tap((builder) => {
        frontFacingSurfaceWidth = builder.getUvBoundingBoxSize().height
      })
      .move('a', 0, -outFacingSurfaceWidth)
      .mirrorY(0)
      .toUv(),
    // 2.1.2. F.lt (4) -> I.lt (8) -> I.rt (9)
    builder
      .planar('z', [4, 8, 9], scalingFactor)
      .move('a', 0, -outFacingSurfaceWidth)
      .mirrorY(0)
      .toUv(),
    // 2.2. Right
    // 2.2.1. F.rt (5) -> I.rb (10) -> F.rb (6)
    builder
      .planar('z', [5, 10, 6], scalingFactor)
      .rotate(THREE.MathUtils.degToRad(90), centerPoint)
      .move('a', 0, -outFacingSurfaceWidth)
      .mirrorY(0)
      .toUv(),
    // 2.2.2. F.rt (5) -> I.rt (9) -> I.rb (10)
    builder
      .planar('z', [5, 9, 10], scalingFactor)
      .rotate(THREE.MathUtils.degToRad(90), centerPoint)
      .move('a', 0, -outFacingSurfaceWidth)
      .mirrorY(0)
      .toUv(),
    // 2.3. Bottom
    // 2.3.1. F.rb (6) -> I.lb (11) -> F.lb (7)
    builder
      .planar('z', [6, 11, 7], scalingFactor)
      .move('a', 0, outFacingSurfaceWidth)
      .mirrorX(0)
      .toUv(),
    // 2.3.2. F.rb (6) -> I.rb (10) -> I.lb (11)
    builder
      .planar('z', [6, 10, 11], scalingFactor)
      .move('a', 0, outFacingSurfaceWidth)
      .mirrorX(0)
      .toUv(),
    // 2.4. Left
    // 2.4.1. F.lb (7) -> I.lt (8) -> F.lt (4)
    builder
      .planar('z', [7, 8, 4], scalingFactor)
      .rotate(THREE.MathUtils.degToRad(90), centerPoint)
      .move('a', 0, outFacingSurfaceWidth)
      .mirrorX(0)
      .toUv(),
    // 2.4.2. F.lb (7) -> I.lb (11) -> I.lt (8)
    builder
      .planar('z', [7, 11, 8], scalingFactor)
      .rotate(THREE.MathUtils.degToRad(90), centerPoint)
      .move('a', 0, outFacingSurfaceWidth)
      .mirrorX(0)
      .toUv(),

    // 3. In-facing surfaces
    // 3.1. Top
    // 3.1.1. I.lt (8) -> A.rt (13) [1] -> I.rt (9)
    builder
      .planar('y', [8, 13, 9], scalingFactor)
      .move(
        'a',
        frontFacingSurfaceWidth,
        -outFacingSurfaceWidth - frontFacingSurfaceWidth,
      )
      .mirrorY(0)
      .toUv(),
    // 3.1.2. I.lt (8) -> A.lt (12) [0] -> A.rt (13) [1]
    builder
      .planar('y', [8, 12, 13], scalingFactor)
      .move(
        'a',
        frontFacingSurfaceWidth,
        -outFacingSurfaceWidth - frontFacingSurfaceWidth,
      )
      .mirrorY(0)
      .toUv(),
    // 3.2. Right
    // 3.2.1. I.rt (9) -> A.rb (14) [2] -> I.rb (10)
    builder
      .planar('x', [9, 14, 10], scalingFactor)
      .rotate(THREE.MathUtils.degToRad(90), centerPoint)
      .move(
        'a',
        frontFacingSurfaceWidth,
        -outFacingSurfaceWidth - frontFacingSurfaceWidth,
      )
      .mirrorY(0)
      .toUv(),
    // 3.2.2. I.rt (9) -> A.rt (13) [1] -> A.rb (14) [2]
    builder
      .planar('x', [9, 13, 14], scalingFactor)
      .rotate(THREE.MathUtils.degToRad(90), centerPoint)
      .move(
        'a',
        frontFacingSurfaceWidth,
        -outFacingSurfaceWidth - frontFacingSurfaceWidth,
      )
      .mirrorY(0)
      .toUv(),
    // 3.3. Bottom
    // 3.3.1. // I.rb (10) -> A.lb (15) [3] -> I.lb (11)
    builder
      .planar('y', [10, 15, 11], scalingFactor)
      .move(
        'a',
        -frontFacingSurfaceWidth,
        -outFacingSurfaceWidth - frontFacingSurfaceWidth,
      )
      .mirrorX(0)
      .mirrorY(0)
      .toUv(),
    // 3.3.2. I.rb (10) -> A.rb (14) [2] -> A.lb (15) [3]
    builder
      .planar('y', [10, 14, 15], scalingFactor)
      .move(
        'a',
        -frontFacingSurfaceWidth,
        -outFacingSurfaceWidth - frontFacingSurfaceWidth,
      )
      .mirrorX(0)
      .mirrorY(0)
      .toUv(),
    // // 3.4. Left
    // // 3.4.1. I.lb (11) -> A.lb (15) [3] -> A.lt (12) [0]
    builder
      .planar('x', [11, 15, 12], scalingFactor)
      .rotate(THREE.MathUtils.degToRad(90), centerPoint)
      .move(
        'a',
        -frontFacingSurfaceWidth,
        -outFacingSurfaceWidth - frontFacingSurfaceWidth,
      )
      .mirrorX(0)
      .mirrorY(0)
      .toUv(),
    // 3.4.2. I.lb (11) -> A.lt (12) [0] -> I.lt (8)
    builder
      .planar('x', [11, 12, 8], scalingFactor)
      .rotate(THREE.MathUtils.degToRad(90), centerPoint)
      .move(
        'a',
        -frontFacingSurfaceWidth,
        -outFacingSurfaceWidth - frontFacingSurfaceWidth,
      )
      .mirrorX(0)
      .mirrorY(0)
      .toUv(),
  ]

  frameGeometry.faceVertexUvs = [[...uvs]]
  frameGeometry.uvsNeedUpdate = true
  frameGeometry.elementsNeedUpdate = true
}

const updatePassepartoutUVs = (passepartoutGeometry: THREE.Geometry) => {
  const builder = new UVBuilder(passepartoutGeometry)

  const uvs = passepartoutGeometry.faces.map((face) => {
    return builder.planar('z', [face.a, face.b, face.c]).toUv()
  })

  passepartoutGeometry.faceVertexUvs = [[...uvs]]

  passepartoutGeometry.uvsNeedUpdate = true
  passepartoutGeometry.elementsNeedUpdate = true
}

const generateFrameVertices = (settings: MainFrameSettings) => {
  const artworkHalfWidth = settings.artworkWidth / 2
  const artworkHalfHeight = settings.artworkHeight / 2
  const borderTopSize = settings.borderTopSize
  const borderRightSize = settings.borderRightSize
  const borderBottomSize = settings.borderBottomSize
  const borderLeftSize = settings.borderLeftSize
  const passepartoutTopSize = settings.passepartoutTopSize
  const passepartoutRightSize = settings.passepartoutRightSize
  const passepartoutBottomSize = settings.passepartoutBottomSize
  const passepartoutLeftSize = settings.passepartoutLeftSize
  const depth = settings.depth
  const inset = settings.inset

  // Vertices, positioned by watching from front.
  // All vertices are laid out in quad fashion, following CSS TRBL order.
  // Names in format of <SET>.<xy>.
  return [
    // BACK (artwork + border + passepartout)
    // 0, BACK, left top (B.lt)
    [
      -artworkHalfWidth - borderLeftSize - passepartoutLeftSize,
      artworkHalfHeight + borderTopSize + passepartoutTopSize,
      0,
    ],
    // 1, BACK, right top (B.rt)
    [
      artworkHalfWidth + borderRightSize + passepartoutRightSize,
      artworkHalfHeight + borderTopSize + passepartoutTopSize,
      0,
    ],
    // 2, BACK, right bottom (B.rb)
    [
      artworkHalfWidth + borderRightSize + passepartoutRightSize,
      -artworkHalfHeight - borderBottomSize - passepartoutBottomSize,
      0,
    ],
    // 3, BACK, left bottom (B.lb)
    [
      -artworkHalfWidth - borderLeftSize - passepartoutLeftSize,
      -artworkHalfHeight - borderBottomSize - passepartoutBottomSize,
      0,
    ],

    // FRONT (artwork + border + passepartout)
    // 4, FRONT, left top (F.lt)
    [
      -artworkHalfWidth - borderLeftSize - passepartoutLeftSize,
      artworkHalfHeight + borderTopSize + passepartoutTopSize,
      depth,
    ],
    // 5, FRONT, right top (F.rt)
    [
      artworkHalfWidth + borderRightSize + passepartoutRightSize,
      artworkHalfHeight + borderTopSize + passepartoutTopSize,
      depth,
    ],
    // 6, FRONT, right bottom (F.rb)
    [
      artworkHalfWidth + borderRightSize + passepartoutRightSize,
      -artworkHalfHeight - borderBottomSize - passepartoutBottomSize,
      depth,
    ],
    // 7, FRONT, left bottom (F.lb)
    [
      -artworkHalfWidth - borderLeftSize - passepartoutLeftSize,
      -artworkHalfHeight - borderBottomSize - passepartoutBottomSize,
      depth,
    ],

    // INSET (artwork + passepartout)
    // 8, INSET, left top (I.lt)
    [
      -artworkHalfWidth - passepartoutLeftSize,
      artworkHalfHeight + passepartoutTopSize,
      depth,
    ],
    // 9, INSET, right top (I.rt)
    [
      artworkHalfWidth + passepartoutRightSize,
      artworkHalfHeight + passepartoutTopSize,
      depth,
    ],
    // 10, INSET, right bottom (I.rb)
    [
      artworkHalfWidth + passepartoutRightSize,
      -artworkHalfHeight - passepartoutBottomSize,
      depth,
    ],
    // 11, INSET, left bottom (I.lb)
    [
      -artworkHalfWidth - passepartoutLeftSize,
      -artworkHalfHeight - passepartoutBottomSize,
      depth,
    ],

    // PASSEPARTOUT (artwork + passepartout)
    // 12, PASSEPARTOUT, left top (P.lt)
    [
      -artworkHalfWidth - passepartoutLeftSize,
      artworkHalfHeight + passepartoutTopSize,
      depth - inset,
    ],
    // 13, PASSEPARTOUT, right top (P.rt)
    [
      artworkHalfWidth + passepartoutRightSize,
      artworkHalfHeight + passepartoutTopSize,
      depth - inset,
    ],
    // 14, PASSEPARTOUT, right bottom (P.rb)
    [
      artworkHalfWidth + passepartoutRightSize,
      -artworkHalfHeight - passepartoutBottomSize,
      depth - inset,
    ],
    // 15, PASSEPARTOUT, left bottom (P.lb)
    [
      -artworkHalfWidth - passepartoutLeftSize,
      -artworkHalfHeight - passepartoutBottomSize,
      depth - inset,
    ],

    // ART (artwork)
    // 16, ART, left top (A.lt)
    [-artworkHalfWidth, artworkHalfHeight, depth - inset],
    // 17, ART, right top (A.rt)
    [artworkHalfWidth, artworkHalfHeight, depth - inset],
    // 18, ART, right bottom (A.rb)
    [artworkHalfWidth, -artworkHalfHeight, depth - inset],
    // 19, ART, left bottom (A.lb)
    [-artworkHalfWidth, -artworkHalfHeight, depth - inset],
  ].map((vector) => new THREE.Vector3(...vector))
}

export const generateFrame = (
  frameGeometry: THREE.Geometry,
  passepartoutGeometry: THREE.Geometry,
  artworkGeometry: THREE.Geometry,
  settings: PartialFullFrameSettings = defaultFrameSettings,
) => {
  const normalizedSettings = Object.assign({}, defaultFrameSettings, settings)
  const { artworkWidth, artworkHeight, generateBackFace } = normalizedSettings

  const vertices = generateFrameVertices(normalizedSettings)

  //
  // Setup frame geometry.
  //

  frameGeometry.vertices.push(...vertices)

  // Frame faces, positioned by face rings.
  // All faces are laid out in ring fashion, following CSS TRBL order.
  const frameFaces = [
    // Out-facing
    // Top
    // B.lt (0) -> F.rt (5) -> B.rt (1)
    [0, 5, 1],
    // F.lt (4) -> F.rt (5) -> B.lt (0)
    [4, 5, 0],
    // Right
    // B.rt (1) -> F.rb (6) -> B.rb (2)
    [1, 6, 2],
    // B.rt (1) -> F.rt (5) -> F.rb (6)
    [1, 5, 6],
    // Bottom
    // B.rb (2) -> F.lb (7) -> B.lb (3)
    [2, 7, 3],
    // B.rb (2) -> F.rb (6) -> F.lb (7)
    [2, 6, 7],
    // Left
    // B.lt (0) -> B.lb (3) -> F.lt (4)
    [0, 3, 4],
    // F.lt (4) -> B.lb (3) -> F.lb (7)
    [4, 3, 7],

    // Front-facing
    // Top
    // F.lt (4) -> I.rt (9) -> F.rt (5)
    [4, 9, 5],
    // F.lt (4) -> I.lt (8) -> I.rt (9)
    [4, 8, 9],
    // Right
    // F.rt (5) -> I.rb (10) -> F.rb (6)
    [5, 10, 6],
    // F.rt (5) -> I.rt (9) -> I.rb (10)
    [5, 9, 10],
    // Bottom
    // F.rb (6) -> I.lb (11) -> F.lb (7)
    [6, 11, 7],
    // F.rb (6) -> I.rb (10) -> I.lb (11)
    [6, 10, 11],
    // Left
    // F.lb (7) -> I.lt (8) -> F.lt (4)
    [7, 8, 4],
    // F.lb (7) -> I.lb (11) -> I.lt (8)
    [7, 11, 8],

    // In-facing
    // Top
    // I.lt (8) -> P.rt (13) [1] -> I.rt (9)
    [8, 13, 9],
    // I.lt (8) -> P.lt (12) [0] -> P.rt (13) [1]
    [8, 12, 13],
    // Right
    // I.rt (9) -> P.rb (14) [2] -> I.rb (10)
    [9, 14, 10],
    // I.rt (9) -> P.rt (13) [1] -> P.rb (14) [2]
    [9, 13, 14],
    // Bottom
    // I.rb (10) -> P.lb (15) [3] -> I.lb (11)
    [10, 15, 11],
    // I.rb (10) -> P.rb (14) [2] -> P.lb (15) [3]
    [10, 14, 15],
    // Left
    // I.lb (11) -> P.lb (15) [3] -> P.lt (12) [0]
    [11, 15, 12],
    // I.lb (11) -> P.lt (12) [0] -> I.lt (8)
    [11, 12, 8],
  ].map((face) => new THREE.Face3(face[0], face[1], face[2]))

  // Add Back Face, if requested.
  if (generateBackFace) {
    const backFaceFaces = [
      [0, 1, 2],
      [3, 0, 2],
    ].map((face) => new THREE.Face3(face[0], face[1], face[2]))

    frameFaces.unshift(...backFaceFaces)
  }

  frameGeometry.faces.push(...frameFaces)

  frameGeometry.computeFaceNormals()
  frameGeometry.computeBoundingBox()

  updateFrameUVs(frameGeometry)

  //
  // Setup passepartout geometry.
  //

  passepartoutGeometry.vertices.push(
    ...[12, 13, 14, 15, 16, 17, 18, 19].map(
      (vertexIndex) => vertices[vertexIndex],
    ),
  )

  const passepartoutFaces = [
    // top
    // P.lt (12) -> A.lt (16) -> P.rt (13) (top triangle)
    [0, 4, 1],
    // P.rt (13) -> A.lt (16) -> A.rt (17) (bottom triangle)
    [1, 4, 5],
    // right
    // P.rt (13) -> A.rt (17) -> P.rb (14) (right triangle)
    [1, 5, 2],
    // P.rb (14) -> A.rt (17) -> A.rb (18) (left triangle)
    [2, 5, 6],
    // bottom
    // P.rb (14) -> A.rb (18) -> P.lb (15) (bottom triangle)
    [2, 6, 3],
    // P.lb (15) -> A.rb (18) -> A.lb (19) (top triangle)
    [3, 6, 7],
    // left
    // P.lb (15) -> A.lb (19) -> P.lt (12) (left triangle)
    [3, 7, 0],
    // P.lt (12) -> A.lb (19) -> A.lt (16) (right triangle)
    [0, 7, 4],
  ].map((face) => new THREE.Face3(face[0], face[1], face[2]))

  passepartoutGeometry.faces.push(...passepartoutFaces)

  passepartoutGeometry.computeFaceNormals()
  passepartoutGeometry.computeBoundingBox()

  //
  // Setup passepartout UV's.
  //

  updatePassepartoutUVs(passepartoutGeometry)

  //
  // Setup preview geometry.
  //

  artworkGeometry.vertices.push(
    ...[16, 17, 18, 19].map((vertexIndex) => vertices[vertexIndex]),
  )

  artworkGeometry.verticesNeedUpdate = true

  const artFaces = [
    // A.lt (16) -> A.rb (18) -> A.rt (17) (right triangle)
    [0, 2, 1],
    // A.lt (16) -> A.lb (19) -> A.rb (18) (left triangle)
    [0, 3, 2],
  ].map((face) => new THREE.Face3(face[0], face[1], face[2]))

  artworkGeometry.faces.push(...artFaces)

  artworkGeometry.computeFaceNormals()

  //
  // Setup art UV's.
  //

  const aspectRatio = artworkWidth / artworkHeight
  const adjustedWidth = 1 * aspectRatio
  const adjustedHeight = 1 / aspectRatio

  let vt = 1
  let hr = 1
  let vb = 0
  let hl = 0

  // Crop horizontal
  if (aspectRatio < 1) {
    const stretchDifference = adjustedWidth - 1
    const stretchWidth = stretchDifference / 2

    hr = 1 + stretchWidth
    hl = 0 - stretchWidth
  }

  // Crop vertical
  if (aspectRatio > 1) {
    const stretchDifference = adjustedHeight - 1
    const stretchHeight = stretchDifference / 2

    vt = 1 + stretchHeight
    vb = 0 - stretchHeight
  }

  artworkGeometry.faceVertexUvs = [
    [
      [
        new THREE.Vector2(hl, vt),
        new THREE.Vector2(hr, vb),
        new THREE.Vector2(hr, vt),
      ],
      [
        new THREE.Vector2(hl, vt),
        new THREE.Vector2(hl, vb),
        new THREE.Vector2(hr, vb),
      ],
    ],
  ]

  artworkGeometry.uvsNeedUpdate = true
  artworkGeometry.elementsNeedUpdate = true
}

export const updateFrame = (
  frameGeometry: THREE.Geometry,
  passepartoutGeometry: THREE.Geometry,
  artworkGeometry: THREE.Geometry,
  settings: PartialMainFrameSettings = defaultFrameSettings,
) => {
  const normalizedSettings = Object.assign({}, defaultFrameSettings, settings)
  const { artworkWidth, artworkHeight } = normalizedSettings

  const vertices = generateFrameVertices(normalizedSettings)

  //
  // Update frame geometry.
  //

  vertices.forEach((vertice, index) => {
    frameGeometry.vertices[index].set(vertice.x, vertice.y, vertice.z)
  })

  frameGeometry.verticesNeedUpdate = true
  frameGeometry.elementsNeedUpdate = true
  frameGeometry.computeFaceNormals()
  frameGeometry.computeBoundingBox()

  updateFrameUVs(frameGeometry)

  //
  // Update passepartout geometry.
  //
  ;[12, 13, 14, 15, 16, 17, 18, 19]
    .map((vertexIndex) => vertices[vertexIndex])
    .forEach((vertice, index) => {
      passepartoutGeometry.vertices[index].set(vertice.x, vertice.y, vertice.z)
    })

  passepartoutGeometry.verticesNeedUpdate = true
  passepartoutGeometry.elementsNeedUpdate = true
  passepartoutGeometry.computeFaceNormals()
  passepartoutGeometry.computeBoundingBox()

  //
  // Update passepartout UV's.
  //

  updatePassepartoutUVs(passepartoutGeometry)

  //
  // Update art geometry.
  //
  ;[16, 17, 18, 19]
    .map((vertexIndex) => vertices[vertexIndex])
    .forEach((vertice, index) => {
      artworkGeometry.vertices[index].set(vertice.x, vertice.y, vertice.z)
    })

  artworkGeometry.verticesNeedUpdate = true
  artworkGeometry.elementsNeedUpdate = true
  artworkGeometry.computeFaceNormals()

  //
  // Update art UV's.
  //

  const aspectRatio = artworkWidth / artworkHeight
  const adjustedWidth = 1 * aspectRatio
  const adjustedHeight = 1 / aspectRatio

  let vt = 1
  let hr = 1
  let vb = 0
  let hl = 0

  // Crop horizontal
  if (aspectRatio < 1) {
    const stretchDifference = adjustedWidth - 1
    const stretchWidth = stretchDifference / 2

    hr = 1 + stretchWidth
    hl = 0 - stretchWidth
  }

  // Crop vertical
  if (aspectRatio > 1) {
    const stretchDifference = adjustedHeight - 1
    const stretchHeight = stretchDifference / 2

    vt = 1 + stretchHeight
    vb = 0 - stretchHeight
  }

  artworkGeometry.faceVertexUvs[0][0][0].set(hl, vt)
  artworkGeometry.faceVertexUvs[0][0][1].set(hr, vb)
  artworkGeometry.faceVertexUvs[0][0][2].set(hr, vt)

  artworkGeometry.faceVertexUvs[0][1][0].set(hl, vt)
  artworkGeometry.faceVertexUvs[0][1][1].set(hl, vb)
  artworkGeometry.faceVertexUvs[0][1][2].set(hr, vb)

  artworkGeometry.uvsNeedUpdate = true
}
